import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Form, Button, ButtonGroup, InputGroup, Modal } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { FiSettings } from 'react-icons/fi';
import { MIN_YEAR, MAX_YEAR } from '../services/ngramProcessor';
import { parseLegacyHash } from '../services/legacyHash';
const DEFAULT_START_TERM = 'demokrati';
const clampYear = (value, min, max) => Math.min(max, Math.max(min, value));
const sanitizeZoomRange = (startCandidate, endCandidate) => {
    const fallbackStart = Number.isFinite(startCandidate) ? startCandidate : MIN_YEAR;
    const fallbackEnd = Number.isFinite(endCandidate) ? endCandidate : MAX_YEAR;
    const clampedStart = clampYear(fallbackStart, MIN_YEAR, MAX_YEAR);
    const clampedEnd = clampYear(fallbackEnd, MIN_YEAR, MAX_YEAR);

    if (clampedStart <= clampedEnd) {
        return { start: clampedStart, end: clampedEnd };
    }

    return { start: clampedEnd, end: clampedStart };
};

const SearchControls = ({ onSearch, onGraphTypeChange, data, settings, onSettingsChange }) => {
    const legacyState = useMemo(() => parseLegacyHash(window.location.hash), []);
    const initialZoomRange = useMemo(
        () => sanitizeZoomRange(legacyState.zoomStart, legacyState.zoomEnd),
        [legacyState.zoomStart, legacyState.zoomEnd]
    );
    const initialWords = legacyState.words.length > 0 ? legacyState.words.join(', ') : '';
    const [words, setWords] = useState(initialWords);
    const [corpus, setCorpus] = useState(legacyState.corpus || 'bok');
    const [lang, setLang] = useState(legacyState.lang || 'nob');
    const [graphType, setGraphType] = useState(legacyState.graphType || 'relative');
    const [showModal, setShowModal] = useState(false);
    const [showLangDropdown, setShowLangDropdown] = useState(false);
    const [showCorpusDropdown, setShowCorpusDropdown] = useState(false);
    const [showGraphTypeDropdown, setShowGraphTypeDropdown] = useState(false);
    const [showToolsModal, setShowToolsModal] = useState(false);
    const [capitalization, setCapitalization] = useState(Boolean(legacyState.capitalization));
    const [smoothing, setSmoothing] = useState(legacyState.smoothing ?? 4);
    const [lineThickness, setLineThickness] = useState(3);
    const [lineTransparency, setLineTransparency] = useState(0.1);
    const [relativeNormalization, setRelativeNormalization] = useState(legacyState.relativeNormalization || 'standard');
    const [curvePattern, setCurvePattern] = useState(Boolean(legacyState.curvePattern));
    const [curvePatternColorMode, setCurvePatternColorMode] = useState(legacyState.curvePatternColorMode || 'black');
    const [palette, setPalette] = useState('standard');
    const palettes = [
        { id: 'standard', label: 'Standard' },
        { id: 'colorblind', label: 'Fargeblindvennlig' },
        { id: 'bw', label: 'Svart/hvitt' }
    ];
    const [scaling, setScaling] = useState(legacyState.scaling || 'auto');
    const [zoomStart, setZoomStart] = useState(initialZoomRange.start);
    const [zoomEnd, setZoomEnd] = useState(initialZoomRange.end);

    useEffect(() => {
        const syncedRange = sanitizeZoomRange(settings?.zoomStart, settings?.zoomEnd);
        if (syncedRange.start !== zoomStart) {
            setZoomStart(syncedRange.start);
        }
        if (syncedRange.end !== zoomEnd) {
            setZoomEnd(syncedRange.end);
        }
    }, [settings?.zoomStart, settings?.zoomEnd, zoomStart, zoomEnd]);

    const emitSettings = useCallback((overrides = {}) => {
        onSettingsChange?.({
            capitalization,
            smoothing,
            lineThickness,
            lineTransparency,
            relativeNormalization,
            curvePattern,
            curvePatternColorMode,
            scaling,
            palette,
            zoomStart,
            zoomEnd,
            ...overrides
        });
    }, [onSettingsChange, capitalization, smoothing, lineThickness, lineTransparency, relativeNormalization, curvePattern, curvePatternColorMode, scaling, palette, zoomStart, zoomEnd]);
    const updateCapitalization = (newValue) => {
        setCapitalization(newValue);
        emitSettings({ capitalization: newValue });
        // Trigger a new search with updated capitalization setting
        if (words) {
            performSearch();
        }
    };

    const latestWordsRef = useRef(words);
    const onSearchRef = useRef(onSearch);
    const langDropdownRef = useRef(null);
    const corpusDropdownRef = useRef(null);
    const graphTypeDropdownRef = useRef(null);

    useEffect(() => {
        latestWordsRef.current = words;
    }, [words]);

    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    useEffect(() => {
        if (!showLangDropdown && !showCorpusDropdown && !showGraphTypeDropdown) {
            return;
        }

        const handlePointerDownOutside = (event) => {
            const target = event.target;
            if (
                !langDropdownRef.current?.contains(target) &&
                !corpusDropdownRef.current?.contains(target) &&
                !graphTypeDropdownRef.current?.contains(target)
            ) {
                setShowLangDropdown(false);
                setShowCorpusDropdown(false);
                setShowGraphTypeDropdown(false);
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowLangDropdown(false);
                setShowCorpusDropdown(false);
                setShowGraphTypeDropdown(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDownOutside);
        document.addEventListener('touchstart', handlePointerDownOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handlePointerDownOutside);
            document.removeEventListener('touchstart', handlePointerDownOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showLangDropdown, showCorpusDropdown, showGraphTypeDropdown]);

    const performSearch = () => {
        const wordList = words.split(',')
            .map(w => w.trim())
            .filter(w => w.length > 0);
        
        if (wordList.length === 0) {
            return;
        }

        onSearchRef.current(wordList, corpus, lang, graphType);
    };

    // Trigger search when parameters change
    useEffect(() => {
        const currentWords = latestWordsRef.current;
        const wordList = currentWords
            .split(',')
            .map(w => w.trim())
            .filter(w => w.length > 0);

        if (wordList.length > 0) {
            onSearchRef.current(wordList, corpus, lang, graphType);
        }
    }, [corpus, lang, graphType]);

    useEffect(() => {
        if (graphType !== 'relative') {
            return;
        }

        const currentWords = latestWordsRef.current;
        const wordList = currentWords
            .split(',')
            .map(w => w.trim())
            .filter(w => w.length > 0);

        if (wordList.length > 0) {
            onSearchRef.current(wordList, corpus, lang, graphType);
        }
    }, [relativeNormalization, corpus, graphType, lang]);

    useEffect(() => {
        if (legacyState.words.length === 0) {
            onSearchRef.current([DEFAULT_START_TERM], corpus, lang, graphType);
            return;
        }

        onGraphTypeChange(legacyState.graphType || 'relative');
        const hydratedZoomRange = sanitizeZoomRange(legacyState.zoomStart, legacyState.zoomEnd);
        emitSettings({
            capitalization,
            smoothing,
            lineThickness,
            lineTransparency,
            relativeNormalization,
            curvePattern,
            curvePatternColorMode,
            scaling,
            palette,
            zoomStart: hydratedZoomRange.start,
            zoomEnd: hydratedZoomRange.end
        });
        // Intentionally run once for legacy hash hydration.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        performSearch();
    };

    const handleGraphTypeSelect = (type) => {
        setGraphType(type);
        onGraphTypeChange(type);
        setShowModal(false);
        setShowGraphTypeDropdown(false);
    };

    const graphTypes = [
        { id: 'relative', label: 'Relative Frequency' },
        { id: 'absolute', label: 'Absolute Frequency' },
        { id: 'cumulative', label: 'Cumulative Frequency' },
        { id: 'cohort', label: 'Cohort Analysis' }
    ];

    const languages = [
        { code: 'nob', label: 'bokmål', fullName: 'bokmål' },
        { code: 'nno', label: 'nynorsk', fullName: 'nynorsk' },
        { code: 'sme', label: 'nordsamisk', fullName: 'davvisámegiella' },
        { code: 'sma', label: 'sørsamisk', fullName: 'åarjelsaemien gïele' },
        { code: 'smj', label: 'lulesamisk', fullName: 'julevsámegiella' },
        { code: 'fkv', label: 'kvensk', fullName: 'kainun kieli' }
    ];
    const selectedLanguage = languages.find((language) => language.code === lang);
    const languageButtonLabel = corpus === 'avis'
        ? 'norsk (avis)'
        : (selectedLanguage?.fullName || lang);
    return (
        <div className="d-flex flex-column flex-md-row gap-3 align-items-start w-100">
            <div className="d-flex flex-column flex-md-row gap-3 align-items-start w-100">
                <Form onSubmit={handleSubmit} className="d-flex align-items-center gap-3 flex-grow-1">
                    <InputGroup className="flex-grow-1">
                        <Form.Control
                            type="text"
                            value={words}
                            onChange={(e) => setWords(e.target.value)}
                            placeholder={DEFAULT_START_TERM}
                            aria-label="Search words"
                            style={{ 
                                minWidth: '200px',
                                flex: '1 1 auto'
                            }}
                        />
                        <Button 
                            variant="outline-secondary" 
                            type="submit"
                            title="Search"
                            style={{
                                backgroundColor: 'white',
                                border: '1px solid #ced4da',
                                color: '#212529'
                            }}
                        >
                            <FaSearch />
                        </Button>
                    </InputGroup>
                </Form>

                    <div className="d-flex gap-2">
                    <div className="dropdown" ref={langDropdownRef}>
                        <button
                            className="btn btn-outline-secondary dropdown-toggle"
                            type="button"
                            onClick={() => {
                                setShowLangDropdown((prev) => !prev);
                                setShowCorpusDropdown(false);
                                setShowGraphTypeDropdown(false);
                            }}
                            style={{ 
                                border: 'none',
                                minWidth: '120px',
                                maxWidth: '170px',
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                            disabled={corpus === 'avis'}
                            title={languageButtonLabel}
                        >
                            {languageButtonLabel}
                        </button>
                        {showLangDropdown && corpus !== 'avis' && (
                            <div className="dropdown-menu show">
                                {languages.map(language => (
                                    <button
                                        key={language.code}
                                        className="dropdown-item"
                                        onClick={() => {
                                            setLang(language.code);
                                            setShowLangDropdown(false);
                                        }}
                                        title={language.fullName}
                                    >
                                        {language.fullName}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <ButtonGroup>
                        <div className="dropdown" ref={corpusDropdownRef}>
                            <button
                                className="btn btn-outline-secondary dropdown-toggle"
                                type="button"
                                onClick={() => {
                                    setShowCorpusDropdown((prev) => !prev);
                                    setShowLangDropdown(false);
                                    setShowGraphTypeDropdown(false);
                                }}
                                style={{ 
                                    border: 'none'
                                }}
                            >
                                {corpus}
                            </button>
                            {showCorpusDropdown && (
                                <div className="dropdown-menu show">
                                    <button
                                        className="dropdown-item"
                                        onClick={() => {
                                            setCorpus('bok');
                                            setShowCorpusDropdown(false);
                                        }}
                                    >
                                        bok
                                    </button>
                                    <button
                                        className="dropdown-item"
                                        onClick={() => {
                                            setCorpus('avis');
                                            setShowCorpusDropdown(false);
                                        }}
                                    >
                                        avis
                                    </button>
                                </div>
                            )}
                        </div>
                    </ButtonGroup>

                    <div className="dropdown" ref={graphTypeDropdownRef}>
                        <button
                            className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center"
                            type="button"
                            onClick={() => {
                                setShowGraphTypeDropdown((prev) => !prev);
                                setShowLangDropdown(false);
                                setShowCorpusDropdown(false);
                            }}
                            style={{ 
                                border: 'none',
                                position: 'relative',
                                zIndex: 1001,
                                minWidth: '120px'
                            }}
                            >
                            {graphType === 'relative' ? 'relativ' :
                             graphType === 'absolute' ? 'absolutt' :
                             graphType === 'cumulative' ? 'kumulativ' :
                             graphType === 'cohort' ? 'kohort' :
                             'kohort'}
                        </button>
                        {showGraphTypeDropdown && (
                            <div className="dropdown-menu show d-flex flex-column" style={{ 
                                position: 'absolute',
                                zIndex: 1000,
                                top: '100%',
                                left: 0,
                                marginTop: '0.125rem',
                                minWidth: '120px'
                            }}>
                                <button className="dropdown-item" onClick={() => {
                                    handleGraphTypeSelect('relative');
                                }}>relativ</button>
                                <button className="dropdown-item" onClick={() => {
                                    handleGraphTypeSelect('absolute');
                                }}>absolutt</button>
                                <button className="dropdown-item" onClick={() => {
                                    handleGraphTypeSelect('cumulative');
                                }}>kumulativ</button>
                                <button className="dropdown-item" onClick={() => {
                                    handleGraphTypeSelect('cohort');
                                }}>kohort</button>
                            </div>
                        )}
                    </div>

                    <div className="d-flex gap-2">
                        <Button 
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowToolsModal(true)}
                            className="icon-button icon-button--toolbar"
                            title="Verktøy og innstillinger"
                            aria-label="Verktøy og innstillinger"
                            style={{ 
                                border: 'none',
                                backgroundColor: 'white'
                            }}
                        >
                            <FiSettings aria-hidden="true" />
                        </Button>
                    </div>
                </div>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select Graph Type</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-grid gap-2">
                        {graphTypes.map(type => (
                            <Button
                                key={type.id}
                                variant={graphType === type.id ? 'primary' : 'outline-primary'}
                                onClick={() => handleGraphTypeSelect(type.id)}
                                className="text-start"
                            >
                                {type.label}
                            </Button>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={showToolsModal} onHide={() => setShowToolsModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Verktøy</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-grid gap-3">
                        <div> 
                            <strong className="help-section-title">Grafinnstillinger</strong>
                            <div style={{ paddingLeft: '1em' }}>
                                <div className="settings-option-card">
                                    <Form.Label>Utjevning av kurve: {smoothing} år</Form.Label>
                                    <div style={{ paddingLeft: '1.1em' }}>
                                        <Form.Range
                                            min={0}
                                            max={20}
                                            value={smoothing}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value);
                                                setSmoothing(newValue);
                                                emitSettings({ smoothing: newValue });
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="settings-option-card">
                                    <Form.Label>Fargepalett</Form.Label>
                                    <Form.Select
                                        value={palette}
                                        onChange={e => {
                                            const newValue = e.target.value;
                                            setPalette(newValue);
                                            emitSettings({ palette: newValue });
                                        }}
                                    >
                                        {palettes.map(p => (
                                            <option key={p.id} value={p.id}>{p.label}</option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="settings-option-card">
                                    <Form.Label>Linjetykkelse: {lineThickness}px</Form.Label>
                                    <div style={{ paddingLeft: '1.1em' }}>
                                        <Form.Range
                                            min={1}
                                            max={10}
                                            value={lineThickness}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value);
                                                setLineThickness(newValue);
                                                emitSettings({ lineThickness: newValue });
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="settings-option-card d-flex align-items-center justify-content-between">
                                    <Form.Label className="mb-0">Kurvemønster</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="curve-pattern-switch"
                                        checked={curvePattern}
                                        onChange={(e) => {
                                            const enabled = e.target.checked;
                                            setCurvePattern(enabled);
                                            emitSettings({ curvePattern: enabled });
                                        }}
                                    />
                                </div>
                                {curvePattern && (
                                    <div className="settings-option-card">
                                        <Form.Label className="mb-2 d-block">Mønsterfarger</Form.Label>
                                        <div>
                                            <Form.Check
                                                type="radio"
                                                id="curve-pattern-color-palette"
                                                name="curve-pattern-color-mode"
                                                label="Behold fargepalett"
                                                checked={curvePatternColorMode === 'palette'}
                                                onChange={() => {
                                                    setCurvePatternColorMode('palette');
                                                    emitSettings({ curvePatternColorMode: 'palette' });
                                                }}
                                            />
                                            <Form.Check
                                                type="radio"
                                                id="curve-pattern-color-black"
                                                name="curve-pattern-color-mode"
                                                label="Svarte linjer"
                                                checked={curvePatternColorMode === 'black'}
                                                onChange={() => {
                                                    setCurvePatternColorMode('black');
                                                    emitSettings({ curvePatternColorMode: 'black' });
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="settings-option-card">
                                    <Form.Label>Transparens: {Math.round(lineTransparency * 100)}%</Form.Label>
                                    <div style={{ paddingLeft: '1.1em' }}>
                                        <Form.Range
                                            min={0}
                                            max={100}
                                            value={lineTransparency * 100}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value) / 100;
                                                setLineTransparency(newValue);
                                                emitSettings({ lineTransparency: newValue });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <strong className="help-section-title">Akse og skala</strong>
                            <div style={{ paddingLeft: '1em' }}>
                                {graphType === 'relative' && (
                                    <div className="settings-option-card">
                                        <Form.Label>Relativt til</Form.Label>
                                        <Form.Select
                                            value={relativeNormalization}
                                            onChange={e => {
                                                const value = e.target.value;
                                                setRelativeNormalization(value);
                                                emitSettings({ relativeNormalization: value });
                                            }}
                                        >
                                            <option value="standard">Total ordmengde (standard)</option>
                                            <option value="functionWords">Funksjonsord-baseline (estimert)</option>
                                        </Form.Select>
                                        <div className="text-muted help-muted" style={{ fontSize: '0.9em', marginTop: '0.4rem' }}>
                                            Funksjonsord-baseline er et estimat og kan være særlig nyttig i perioder med OCR-støy.
                                        </div>
                                    </div>
                                )}
                                <div className="settings-option-card">
                                    <Form.Label>Skalering av y-aksen</Form.Label>
                                    <Form.Select
                                        value={scaling}
                                        onChange={e => {
                                            const value = e.target.value;
                                            setScaling(value);
                                            emitSettings({ scaling: value });
                                        }}
                                    >
                                        <option value="auto">Auto (% eller ppm)</option>
                                        <option value="100">Prosent (%)</option>
                                        <option value="1000000">PPM</option>
                                    </Form.Select>
                                    <div className="text-muted help-muted" style={{ fontSize: '0.9em', marginTop: '0.4rem' }}>
                                        Auto velger prosent eller ppm basert på datanivå.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <strong className="help-section-title">Søk-innstillinger</strong>
                            <div style={{ paddingLeft: '1em' }}>
                                <div className="settings-option-card d-flex align-items-center justify-content-between">
                                    <Form.Label className="mb-0">Skill mellom stor og liten forbokstav</Form.Label>
                                    <Form.Check 
                                        type="switch"
                                        id="capitalization-switch"
                                        checked={capitalization}
                                        onChange={(e) => updateCapitalization(e.target.checked)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default SearchControls; 